<script setup lang="ts">
import { ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useAppStore } from '@/store/modules/app';
import { useThemeStore } from '@/store/modules/theme';
import { NSpin } from 'naive-ui';
import { $t } from '@/locales';
import ErrorBoundary from '@/components/support/common/ErrorBoundary.vue';

defineOptions({
    name: 'GlobalContent'
});

interface Props {
    /** Show padding for content */
    showPadding?: boolean;
}

withDefaults(defineProps<Props>(), {
    showPadding: true
});

const appStore = useAppStore();
const themeStore = useThemeStore();
const currentRoute = useRoute();

// Track suspense and error states
const suspenseState = ref<'idle' | 'pending' | 'resolved' | 'fallback'>('idle');
const errorBoundaryRef = ref<InstanceType<typeof ErrorBoundary> | null>(null);

// Reset error boundary when route changes
watch(() => currentRoute.path, () => {
    if (errorBoundaryRef.value) {
        errorBoundaryRef.value.reset();
    }
    suspenseState.value = 'idle';
});

// Suspense event handlers
const onSuspensePending = () => {
    suspenseState.value = 'pending';
};

const onSuspenseResolve = () => {
    suspenseState.value = 'resolved';
};

const onSuspenseFallback = () => {
    suspenseState.value = 'fallback';
};

// Handle error from ErrorBoundary
const onComponentError = (error: Error, errorInfo: any) => {
    // Error is already handled by ErrorBoundary
    // This callback can be used for additional logging or analytics
};

// Handle retry from ErrorBoundary
const onRetry = (attempt: number) => {
    // Retry is handled by ErrorBoundary's internal logic
    // This callback can be used for additional tracking
};

// Transition lifecycle handlers
const onAfterEnter = () => {
    appStore.setContentXScrollable(false);
};

const onBeforeLeave = () => {
    appStore.setContentXScrollable(true);
};
</script>

<template>
    <RouterView v-slot="{ Component, route }">
        <!-- Vue recommended nesting order: RouterView → Transition → ErrorBoundary → Suspense → component -->
        <template v-if="Component">
            <Transition 
                :name="themeStore.page.animateMode" 
                mode="out-in"
                @after-enter="onAfterEnter"
                @before-leave="onBeforeLeave"
            >
                <ErrorBoundary
                    ref="errorBoundaryRef"
                    :key="route.path"
                    fallback-type="error"
                    :show-notification="true"
                    :auto-retry="false"
                    :max-retries="3"
                    @error="onComponentError"
                    @retry="onRetry"
                >
                    <Suspense 
                        :timeout="100"
                        @pending="onSuspensePending"
                        @resolve="onSuspenseResolve"
                        @fallback="onSuspenseFallback"
                    >
                        <component 
                            :is="Component" 
                            v-if="appStore.reloadFlag" 
                            :key="route.path"
                            class="global-content-wrapper" 
                            :class="[{ 'with-padding': showPadding }]" 
                        />
                        <template #fallback>
                            <div class="suspense-loading-container">
                                <NSpin size="large">
                                    <template #description>
                                        {{ $t('common.pageLoading') }}
                                    </template>
                                </NSpin>
                            </div>
                        </template>
                    </Suspense>
                </ErrorBoundary>
            </Transition>
        </template>
        
        <!-- Fallback when no Component -->
        <template v-else>
            <div class="no-component-fallback">
                <div class="text-gray-400">{{ $t('common.pageLoadError') }}</div>
            </div>
        </template>
    </RouterView>
</template>

<style scoped>
.suspense-loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 16rem;
    padding: 2rem;
}

.no-component-fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 16rem;
    padding: 2rem;
}
</style>
